
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// module components
import TranscriptFile from '@/modules/BillingModule/components/TranscriptFile/index.vue'
import TranscriptCalculation from '@/modules/BillingModule/components/TranscriptCalculation/index.vue'
import OneCFile from '@/modules/BillingModule/components/OneCFile/index.vue'

// types
import { TranscriptT } from '@/core/types/billing.types'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'

// realisations
import { billing } from '@/core/realisations/billing.realisation'
import { contracts } from '@/core/realisations/contracts.realisation'

import { formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'failed-fetch': FailedFetch,
        loader: Loader,
        'transcript-file': TranscriptFile,
        'transcript-calculation': TranscriptCalculation,
        'one-c-file': OneCFile,
    },
    setup() {
        const { havePermissions, havePermission } = useAccount()
        const notifications = useNotifications()
        const route = useRoute()
        const { saveFile } = useFile()

        const transcript_id = Number(route.params.transcript_id as string)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            transcript: null as TranscriptT | null,
        })

        const loadTranscript = async () => {
            try {
                state.is_loading = true

                const response = await billing.getTranscript(transcript_id)
                state.transcript = response.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении данных транскрипта произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const sendTranscript = async () => {
            if (!state.transcript || !state.transcript.company || !state.transcript.calculations) {
                return
            }

            try {
                state.is_loading = true

                const payload = {
                    actNumber: state.transcript.actNumber,
                    actDate: state.transcript.actFrom1CCreatedAt,
                    transcriptId: transcript_id,
                    invoiceNumber: state.transcript.calculations[0].invoiceNumber,
                    companyId: state.transcript.company.id,
                }

                await contracts.sendTranscript(payload)

                notifications.notify({
                    title: 'Транскрипт отправлен на подпись',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При отправке транскрипта произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const downloadTranscript = async () => {
            try {
                state.is_loading = true

                const response = await contracts.downloadTranscript(transcript_id)
                saveFile(response, `Транскрипт (ID: ${transcript_id})`)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При скачивании транскрипта произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const isCanSend = computed(() =>
            state.transcript
                ? state.transcript.calculations.length === 1 &&
                  havePermission('contract-dogovor24-transcript')
                : false
        )

        const isCanDownloadTranscript = computed(() => {
            return (
                state.transcript &&
                state.transcript.countOfSignatoriesSigners ===
                    state.transcript.totalCountOfSignatories &&
                state.transcript.totalCountOfSignatories
            )
        })

        onBeforeMount(loadTranscript)

        return {
            transcript_id,
            state,
            loadTranscript,
            havePermissions,
            sendTranscript,
            downloadTranscript,
            //
            isCanSend,
            isCanDownloadTranscript,
            formatDateFromBackend,
        }
    },
})
